// src/index.ts
import {
  createBlockDialog,
  openPopup as p,
  runPopup,
  authWithPopup,
  buildJoyIDAuthURL,
  authCallback,
  signMessageWithPopup,
  buildJoyIDSignMessageURL,
  signMessageCallback as signCallback,
  buildJoyIDURL,
  internalConfig,
  DappRequestType,
  getRedirectResponse,
  safeExec
} from "@joyid/common";
var openPopup = p;
var JOYID_STORAGE_KEY = "joyid:bitcoin::account";
var buildSignPsbtURL = (config, type = "popup") => buildJoyIDURL(
  {
    ...internalConfig,
    ...config
  },
  type,
  "/sign-psbt"
);
var buildSignPsbtsURL = (config, type = "popup") => buildJoyIDURL(
  {
    ...internalConfig,
    ...config
  },
  type,
  "/batch-sign-psbt"
);
var getRequestNetwork = (requestAddressType) => requestAddressType == null ? "btc-auto" : `btc-${requestAddressType}`;
var buildConnectUrl = ({ requestAddressType, ...config }, type = "popup") => buildJoyIDAuthURL(
  {
    ...internalConfig,
    ...config,
    requestNetwork: getRequestNetwork(requestAddressType)
  },
  type
);
var buildSignMessageUrl = ({
  message,
  ...config
}, type = "popup") => {
  if (config.signMessageType == null) {
    config.signMessageType = "ecdsa";
  }
  return buildJoyIDSignMessageURL(
    {
      ...internalConfig,
      ...config,
      requestNetwork: "btc-p2tr",
      challenge: message
    },
    type
  );
};
var initConfig = (config) => {
  Object.assign(internalConfig, config);
  return internalConfig;
};
var getConfig = () => internalConfig;
var connectedAccount = {
  address: null,
  pubkey: null
};
var requestAccounts = async () => {
  const config = getConfig();
  const requestNetwork = getRequestNetwork(config.requestAddressType);
  const res = await authWithPopup({
    ...config,
    redirectURL: location.href,
    requestNetwork
  });
  const account = (() => {
    if (requestNetwork === "btc-auto") {
      return res.btcAddressType === "p2wpkh" ? res.nativeSegwit : res.taproot;
    }
    return requestNetwork === "btc-p2wpkh" ? res.nativeSegwit : res.taproot;
  })();
  connectedAccount.address = account.address;
  connectedAccount.pubkey = account.pubkey;
  safeExec(() => {
    localStorage.setItem(JOYID_STORAGE_KEY, JSON.stringify(connectedAccount));
  });
  return [connectedAccount.address];
};
var getAccounts = () => connectedAccount.address == null ? safeExec(() => {
  const json = localStorage.getItem(JOYID_STORAGE_KEY);
  if (json) {
    const account = JSON.parse(json);
    if (account?.address) {
      return [account.address];
    }
  }
  return [];
}) ?? [] : [connectedAccount.address];
var getPublicKey = () => {
  if (connectedAccount.pubkey) {
    return connectedAccount.pubkey;
  }
  return safeExec(() => {
    const json = localStorage.getItem(JOYID_STORAGE_KEY);
    if (json) {
      const account = JSON.parse(json);
      if (account?.pubkey) {
        return account.pubkey;
      }
    }
    return null;
  });
};
var ensureSignerAddress = (signerAddress) => {
  let address = signerAddress;
  if (!address) {
    const [connected] = getAccounts();
    if (connected) {
      address = connected;
    }
  }
  if (!address) {
    throw new Error("signerAddress is required");
  }
  return address;
};
var signMessage = async (message, type, signerAddress) => {
  const config = getConfig();
  signerAddress = ensureSignerAddress(signerAddress);
  const res = await signMessageWithPopup({
    requestNetwork: getRequestNetwork(config.requestAddressType),
    challenge: message,
    address: signerAddress,
    signMessageType: type ?? "ecdsa",
    redirectURL: location.href,
    ...config
  });
  return res.signature;
};
var signPsbtBase = async (psbt, options, signerAddress, isSend) => {
  const config = getConfig();
  signerAddress = ensureSignerAddress(signerAddress);
  const popup = openPopup("");
  if (!popup) {
    return createBlockDialog(
      async () => signPsbtBase(psbt, options, signerAddress, isSend)
    );
  }
  const { timeoutInSeconds, ...dappConfig } = config;
  popup.location.href = buildSignPsbtURL({
    ...dappConfig,
    signerAddress,
    redirectURL: location.href,
    options,
    isSend,
    tx: psbt
  });
  const res = await runPopup({
    timeoutInSeconds: timeoutInSeconds ?? 5e3,
    popup,
    type: DappRequestType.SignPsbt
  });
  return res.tx;
};
var signPsbts = async (psbts, options, signerAddress) => {
  const config = getConfig();
  signerAddress = ensureSignerAddress(signerAddress);
  const popup = openPopup("");
  if (!popup) {
    return createBlockDialog(
      async () => signPsbts(psbts, options, signerAddress)
    );
  }
  const { timeoutInSeconds, ...dappConfig } = config;
  popup.location.href = buildSignPsbtsURL({
    ...dappConfig,
    signerAddress,
    redirectURL: location.href,
    options,
    psbts
  });
  const res = await runPopup({
    timeoutInSeconds: timeoutInSeconds ?? 5e3,
    popup,
    type: DappRequestType.BatchSignPsbt
  });
  return res.psbts;
};
var signPsbt = async (psbt, options, signerAddress) => signPsbtBase(psbt, options, signerAddress, false);
var sendPsbt = async (psbt, options, signerAddress) => signPsbtBase(psbt, options, signerAddress, true);
var connectCallback = (uri, addressType) => {
  const { state, taproot, nativeSegwit, btcAddressType } = authCallback(uri);
  const config = getConfig();
  const account = (() => {
    const requestAddressType = addressType ?? config.requestAddressType;
    if (requestAddressType === "auto") {
      return btcAddressType === "p2wpkh" ? nativeSegwit : taproot;
    }
    return requestAddressType === "p2wpkh" ? nativeSegwit : taproot;
  })();
  safeExec(() => {
    localStorage.setItem(JOYID_STORAGE_KEY, JSON.stringify(account));
  });
  if (state) {
    return {
      state,
      ...account
    };
  }
  return account;
};
var signMessageCallback = (uri) => {
  const { signature, state } = signCallback(uri);
  if (state) {
    return {
      state,
      signature
    };
  }
  return {
    signature
  };
};
var signPsbtCallback = (uri) => {
  const { tx, state } = getRedirectResponse(uri);
  if (state) {
    return {
      state,
      tx
    };
  }
  return {
    tx
  };
};
export {
  buildConnectUrl,
  buildSignMessageUrl,
  buildSignPsbtURL,
  buildSignPsbtsURL,
  connectCallback,
  getAccounts,
  getConfig,
  getPublicKey,
  initConfig,
  openPopup,
  requestAccounts,
  sendPsbt,
  signMessage,
  signMessageCallback,
  signPsbt,
  signPsbtCallback,
  signPsbts
};
//# sourceMappingURL=index.mjs.map